/**
 * wallReturn — the wall's loaded page set and its scroll offset, kept for the length of the tab so
 * that leaving for a pack page and coming back lands the reader exactly where they were
 * (operator-observed defect, 2026-09-01: the wall came back at the top).
 *
 * Why anything is stored at all: only the FIRST page of the wall comes from the route loader
 * (`products/toile/routes/_layout._index.tsx`); every page after it is client state held by
 * `useWallPages`. The pack label is a `<Link>`, so the Back is a POP inside the same document and
 * the wall route simply re-mounts — verified on toile dev 2026-09-01, where a Back left 24 tiles
 * and a 1154px document behind. Any scroll restoration then has nothing to scroll to: it clamps to
 * the bottom of page one.
 *
 * A snapshot is identified by three things together and restored only when all three match: the
 * house, the history entry it was taken on (`location.key` — a fresh visit to `/` gets a new key
 * and therefore no restore, while a Back returns to the entry with its own key intact, including
 * after the `?q=` mirror's `replaceState`), and the query the tiles answer. `sessionStorage`, not a
 * module-level map, because the Back may arrive as a fresh document load: the return must not
 * depend on the page staying alive, nor on bfcache.
 */
import type {TileSummary} from '@core/wall/contract'

export const WALL_RETURN_STORAGE_KEY = 'wall_return'

/** Past this the snapshot is not written — 30 pages of `GET /wall`. A reader that deep is beyond
 *  what a session store should be asked to carry, and the wall then behaves as it did before. */
export const MAX_RETURN_TILES = 720

export type WallReturn = {
    house: string
    /** The `location.key` of the history entry the wall was read on. */
    locationKey: string
    /** The query the tiles answer — `useWallPages`' `loadedQuery`, never the one being typed. */
    q: string
    tiles: TileSummary[]
    nextCursor: string | null
    seamAt: number | null
    matchedCount: number | null
    scrollY: number
}

export type WallReturnIdentity = Pick<WallReturn, 'house' | 'locationKey' | 'q'>

/** The tab's own store, or null where there is none to have: the server render, and the browsers
 *  that throw on the accessor rather than return an empty store. */
export function sessionStore(): Storage | null {
    if (typeof window === 'undefined') return null
    try {
        return window.sessionStorage
    } catch {
        return null
    }
}

export function writeWallReturn(storage: Storage | null, snapshot: WallReturn): void {
    if (!storage) return
    if (snapshot.tiles.length === 0 || snapshot.tiles.length > MAX_RETURN_TILES) return
    try {
        storage.setItem(WALL_RETURN_STORAGE_KEY, JSON.stringify(snapshot))
    } catch {
        // A full or unavailable store costs the return and nothing else: whatever is already there
        // belongs to an older read of the same wall, so the worst it can do is restore fewer pages.
    }
}

export function readWallReturn(storage: Storage | null, want: WallReturnIdentity): WallReturn | null {
    if (!storage) return null
    let raw: string | null
    try {
        raw = storage.getItem(WALL_RETURN_STORAGE_KEY)
    } catch {
        return null
    }
    if (!raw) return null

    let parsed: unknown
    try {
        parsed = JSON.parse(raw)
    } catch {
        return null
    }
    if (!parsed || typeof parsed !== 'object') return null

    const snapshot = parsed as Partial<WallReturn>
    if (snapshot.house !== want.house) return null
    if (snapshot.locationKey !== want.locationKey) return null
    if (snapshot.q !== want.q) return null
    if (!Array.isArray(snapshot.tiles) || snapshot.tiles.length === 0) return null
    if (typeof snapshot.scrollY !== 'number' || !Number.isFinite(snapshot.scrollY)) return null

    return {
        house: snapshot.house,
        locationKey: snapshot.locationKey,
        q: snapshot.q,
        tiles: snapshot.tiles,
        nextCursor: typeof snapshot.nextCursor === 'string' ? snapshot.nextCursor : null,
        seamAt: typeof snapshot.seamAt === 'number' ? snapshot.seamAt : null,
        matchedCount: typeof snapshot.matchedCount === 'number' ? snapshot.matchedCount : null,
        scrollY: Math.max(0, snapshot.scrollY),
    }
}
